import type { DocumentMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  getCantileverDocumentEntryId,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { DOCUMENT_METADATA_FIELD_CONFIG } from 'features/knowledgeDocumentViewer/utils/documentMetadataFieldKeys'
import { mergeDocumentMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import type {
  MetadataCardField,
  MetadataDisplayField,
  MetadataInternalIdsOptions,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'

export const SYSTEM_DOCUMENT_ENTRY_ID_LABEL = 'System Document Entry ID'
export const CANTILEVER_DOCUMENT_ENTRY_ID_LABEL = 'Cantilever Document Entry ID'
export const PAGES_FIELD_LABEL = 'Pages'

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function pushIdField(
  fields: MetadataDisplayField[],
  label: string,
  value: string | null | undefined
): void {
  const trimmed = nonEmpty(value)
  if (trimmed) {
    fields.push({ label, value: trimmed })
  }
}

/** Gecko-internal Rolodex/cantilever IDs; omit from customer-facing metadata. */
export function getInternalDocumentIdDisplayFields(ids: {
  systemDocumentEntryId?: string | null
  cantileverDocumentEntryId?: string | null
}): MetadataDisplayField[] {
  const fields: MetadataDisplayField[] = []
  pushIdField(fields, SYSTEM_DOCUMENT_ENTRY_ID_LABEL, ids.systemDocumentEntryId)
  pushIdField(
    fields,
    CANTILEVER_DOCUMENT_ENTRY_ID_LABEL,
    ids.cantileverDocumentEntryId
  )
  return fields
}

function getDocumentMetadataFields(
  document: SystemDocumentEntry | undefined,
  userEditValues?: DocumentMetadataUserEditValues | null
): MetadataCardField[] {
  const merged = mergeDocumentMetadataValues(
    document?.entry.data,
    userEditValues ?? undefined
  )
  return DOCUMENT_METADATA_FIELD_CONFIG.map((field) => ({
    label: field.label,
    value: nonEmpty(merged[field.key]),
  }))
}

function formatDocumentPageCount(
  totalPages: number | undefined
): string | null {
  if (typeof totalPages !== 'number' || !(totalPages > 0)) return null
  return String(totalPages)
}

export function getDocumentMetadataDisplayFields(
  document: SystemDocumentEntry | undefined,
  userEditValues?: DocumentMetadataUserEditValues | null,
  options?: MetadataInternalIdsOptions
): MetadataDisplayField[] {
  const fields = getDocumentMetadataFields(document, userEditValues).flatMap(
    ({ label, value }) => (value ? [{ label, value }] : [])
  )
  const pageCount = formatDocumentPageCount(document?.entry.data.total_pages)
  if (pageCount) {
    fields.push({ label: PAGES_FIELD_LABEL, value: pageCount })
  }
  if (!options?.includeInternalIds) return fields
  return [
    ...fields,
    ...getInternalDocumentIdDisplayFields({
      systemDocumentEntryId: document?.entry.id,
      cantileverDocumentEntryId: document
        ? getCantileverDocumentEntryId(document)
        : undefined,
    }),
  ]
}

export function getDocumentMetadataCardFields(
  document: SystemDocumentEntry | undefined,
  userEditValues?: DocumentMetadataUserEditValues | null
): MetadataCardField[] {
  return getDocumentMetadataFields(document, userEditValues)
}
